import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  Clock,
  ExternalLink,
  Minus,
  Phone,
  Plus,
} from "lucide-react";
import { Buffer } from "buffer";
import { Transaction, TransactionInstruction, PublicKey } from "@solana/web3.js";
import { BASE_URL } from "../../../config/config";
import { getPhantomProvider, connection } from "../../../utils/phantom.js";

/*
 * ASSUMED request/response shapes — same conventions as the marketplace
 * flow already built on PropertyDetails.jsx (see that file's own header
 * comment for the full list). Please correct if your backend differs:
 *
 *   POST /investor/secondary/me/properties/{id}/listings
 *     body: { quantity, price_per_token_aed, wallet_address }
 *     resp: { id, instruction }
 *
 *   POST /investor/secondary/me/listings/{id}/confirm
 *     body: { signature }
 *
 * PLATFORM FEE — REMOVED:
 * There is no platform fee. A listing is created at the full market
 * price (price_per_block_aed), and a buyer who fulfills it pays that
 * full price directly to the seller — no cut taken anywhere. The
 * proceeds figures shown throughout this flow are the real, full
 * amount the seller actually receives on-chain.
 */

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency: "AED",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
    Number(value || 0),
  );

// FastAPI validation errors return detail as an ARRAY of {type, loc, msg,
// input} objects, not a string — rendering that array directly in JSX
// crashes React ("Objects are not valid as a React child"). This safely
// extracts a readable string in every shape the backend might send.
const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  }
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return error?.message || fallback;
};

const STEPS = [
  { key: "select", label: "Select" },
  { key: "review", label: "Review" },
  { key: "confirm", label: "Confirm" },
  { key: "complete", label: "Complete" },
];

const SellBlocks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const propertyId = location.state?.id;

  const [step, setStep] = useState(1); // 1-indexed, matches STEPS array
  const [portfolio, setPortfolio] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [blocksToSell, setBlocksToSell] = useState(1);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [walletAddress, setWalletAddress] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [listingResult, setListingResult] = useState(null);

  const [existingListing, setExistingListing] = useState(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);
  const [isCancellingExisting, setIsCancellingExisting] = useState(false);

  const property = portfolio?.property;
  const holding = portfolio?.holding;
  const blocksHeld = Number(holding?.blocks_held || 0);
  const pricePerBlock = Number(property?.price_per_block_aed || 0);

  useEffect(() => {
    const provider = getPhantomProvider();
    if (provider?.publicKey) {
      setWalletAddress(provider.publicKey.toString());
    }
  }, []);

  // Check for an existing active listing BEFORE letting the investor start
  // the wizard at all. Without this, a create-listing attempt that got
  // interrupted mid-flow (e.g. signing failed) leaves a real active
  // listing server-side, and starting a NEW one later fails with
  // "You already have an active listing for this property" — confusing
  // if the person has no idea one already exists.
  useEffect(() => {
    let cancelled = false;
    const checkExistingListing = async () => {
      if (!propertyId || !walletAddress) {
        setIsCheckingExisting(false);
        return;
      }
      setIsCheckingExisting(true);
      try {
        const token = localStorage.getItem("rwatoken");
        const response = await axios.get(
          `${BASE_URL}/investor/secondary/properties/${propertyId}/listings/${walletAddress}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        // Same race-condition guard as PropertyDetails.jsx — if this
        // effect re-ran (property/wallet changed) before this response
        // came back, `cancelled` is true and we must NOT apply stale data.
        if (cancelled) return;
        const data = response?.data;
        const listing = data?.listing !== undefined ? data.listing : data;
        setExistingListing(listing || null);
      } catch (err) {
        console.log("Error checking for existing listing:", err);
        if (!cancelled) setExistingListing(null);
      } finally {
        if (!cancelled) setIsCheckingExisting(false);
      }
    };
    checkExistingListing();
    return () => {
      cancelled = true;
    };
  }, [propertyId, walletAddress]);

  const handleCancelExistingListing = async () => {
    if (!existingListing?.id) return;
    setIsCancellingExisting(true);
    const token = localStorage.getItem("rwatoken");
    try {
      const response = await axios.post(
        `${BASE_URL}/investor/secondary/me/listings/${existingListing.id}/cancel`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      const signature = await signAndSendListingInstruction(
        response?.data?.instruction,
      );
      await axios.post(
        `${BASE_URL}/investor/secondary/me/confirm`,
        { type: "listing", id: existingListing.id, signature },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      setExistingListing(null);
    } catch (err) {
      console.log("Cancel existing listing error:", err);
      setSubmitError(
        err?.code === 4001
          ? "You rejected the transaction in Phantom."
          : getErrorMessage(err, "Could not cancel your existing listing."),
      );
    } finally {
      setIsCancellingExisting(false);
    }
  };

  useEffect(() => {
    const fetchPortfolio = async () => {
      if (!propertyId) {
        setLoadError("No property specified.");
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("rwatoken");
        const response = await axios.get(
          `${BASE_URL}/investor/me/portfolio/${propertyId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setPortfolio(response?.data || null);
        const held = Number(response?.data?.holding?.blocks_held || 0);
        setBlocksToSell(held > 0 ? 1 : 0);
      } catch (err) {
        console.log("Failed to fetch portfolio for sell flow:", err);
        setLoadError(getErrorMessage(err, "Unable to load your holding."));
      } finally {
        setIsLoading(false);
      }
    };
    fetchPortfolio();
  }, [propertyId]);

  /* ---------------- Calculations ---------------- */

  const grossValue = useMemo(
    () => blocksToSell * pricePerBlock,
    [blocksToSell, pricePerBlock],
  );
  const proceeds = grossValue;

  const remainingBlocks = Math.max(blocksHeld - blocksToSell, 0);
  const remainingOwnership =
    property?.total_tokens > 0
      ? (remainingBlocks / Number(property.total_tokens)) * 100
      : 0;
  const ownershipShareBeingSold =
    property?.total_tokens > 0
      ? (blocksToSell / Number(property.total_tokens)) * 100
      : 0;

  const setQuickSelect = (pct) => {
    if (pct === "all") {
      setBlocksToSell(blocksHeld);
      return;
    }
    const computed = Math.max(1, Math.round(blocksHeld * pct));
    setBlocksToSell(Math.min(computed, blocksHeld));
  };

  /* ---------------- Sign & submit ---------------- */

  const signAndSendListingInstruction = async (rawInstruction) => {
    const provider = getPhantomProvider();
    if (!provider) {
      throw new Error(
        "Phantom Wallet is not installed. Please install the Phantom browser extension.",
      );
    }

    if (!provider.publicKey) {
      const response = await provider.connect();
      const publicKey = response?.publicKey || provider.publicKey;
      if (!publicKey) {
        throw new Error("Phantom connected, but no wallet address was returned.");
      }
      setWalletAddress(publicKey.toString());
    }

    if (
      !rawInstruction?.programId ||
      !Array.isArray(rawInstruction?.keys) ||
      !rawInstruction?.data
    ) {
      throw new Error("The server did not return a valid Solana instruction.");
    }

    const phantomPublicKey = provider.publicKey;

    const ix = new TransactionInstruction({
      programId: new PublicKey(rawInstruction.programId),
      keys: rawInstruction.keys.map((key) => ({
        pubkey: new PublicKey(key.pubkey),
        isSigner: Boolean(key.isSigner),
        isWritable: Boolean(key.isWritable),
      })),
      data: Buffer.from(rawInstruction.data, "base64"),
    });

    const transaction = new Transaction();
    transaction.add(ix);
    transaction.feePayer = phantomPublicKey;

    const latestBlockhash = await connection.getLatestBlockhash("confirmed");
    transaction.recentBlockhash = latestBlockhash.blockhash;

    // Simulate directly against devnet ourselves BEFORE handing off to
    // Phantom — this gets us the real Anchor program logs (the same
    // level of detail we've only gotten from server-side Python calls
    // all night), instead of Phantom's simplified "reverted during
    // simulation" summary with no further detail.
    try {
      const simResult = await connection.simulateTransaction(transaction);
      console.log("=== RAW DEVNET SIMULATION RESULT ===", simResult);
      console.log("=== SIMULATION LOGS ===", simResult?.value?.logs);
      console.log("=== SIMULATION ERROR (if any) ===", simResult?.value?.err);
    } catch (simErr) {
      console.log("Simulation call itself failed:", simErr);
    }

    const result = await provider.signAndSendTransaction(transaction);
    const signature = typeof result === "string" ? result : result?.signature;
    if (!signature) {
      throw new Error("Phantom returned no transaction signature.");
    }

    const confirmResult = await connection.confirmTransaction(
      {
        signature,
        blockhash: latestBlockhash.blockhash,
        lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
      },
      "confirmed",
    );

    // See PropertyDetails.jsx's signAndSendMarketplaceInstruction for why
    // this explicit check matters — confirmTransaction does NOT throw on
    // a reverted (e.g. vesting-locked) transaction on its own.
    const onChainError = confirmResult?.value?.err;
    if (onChainError) {
      const customCode = onChainError?.InstructionError?.[1]?.Custom;
      if (customCode === 6013) {
        throw new Error(
          "These tokens are still within their vesting lock-up period and can't be listed yet.",
        );
      }
      throw new Error(
        `Transaction was rejected on-chain: ${JSON.stringify(onChainError)}`,
      );
    }

    return signature;
  };

  const handleListForSale = async () => {
    if (!agreedToTerms || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError("");
    const token = localStorage.getItem("rwatoken");

    try {
      const provider = getPhantomProvider();
      let currentWallet = walletAddress;
      if (!provider?.publicKey) {
        const response = await provider?.connect();
        const publicKey = response?.publicKey || provider?.publicKey;
        currentWallet = publicKey?.toString() || "";
        setWalletAddress(currentWallet);
      }

      const response = await axios.post(
        `${BASE_URL}/investor/secondary/me/properties/${property.id}/listings`,
        {
          quantity: Number(blocksToSell),
          price_per_token_aed: pricePerBlock,
          wallet_address: currentWallet,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const listingId =
        response?.data?.id ??
        response?.data?.listing_id ??
        response?.data?.listingId ??
        response?.data?.listing?.id;
      const signature = await signAndSendListingInstruction(
        response?.data?.instruction,
      );

      await axios.post(
        `${BASE_URL}/investor/secondary/me/confirm`,
        { type: "listing", id: listingId, signature },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setListingResult({
        id: listingId,
        blocks: blocksToSell,
        proceeds: proceeds,
        signature,
      });
      setStep(4);
    } catch (error) {
      console.log("List for sale error:", error);
      const message = getErrorMessage(error, "Could not list your tokens. Please try again.");
      if (message.toLowerCase().includes("already have an active listing")) {
        // Same race condition the pre-check exists to avoid — if it still
        // happened, re-fetch so the "cancel existing listing" screen shows
        // instead of leaving the person stuck on a generic error. This
        // re-fetch itself can fail (e.g. the backend's redirect issue) —
        // if it does, we still KNOW a listing exists (that's literally
        // what this 400 means), so fall back to a minimal placeholder
        // rather than let the failure crash the page.
        setIsCheckingExisting(true);
        try {
          const token = localStorage.getItem("rwatoken");
          const response = await axios.get(
            `${BASE_URL}/investor/secondary/properties/${property.id}/listings/${walletAddress}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const data = response?.data;
          setExistingListing(data?.listing !== undefined ? data.listing : data);
        } catch (fetchErr) {
          console.log("Could not fetch existing listing details, showing fallback:", fetchErr);
          setExistingListing({ active: true, quantity: null });
        } finally {
          setIsCheckingExisting(false);
        }
        return;
      }
      setSubmitError(error?.code === 4001 ? "You rejected the transaction in Phantom." : message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------------- Loading / error ---------------- */

  if (isLoading || isCheckingExisting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fdfbf5]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#dfe8d3] border-t-[#4c7a2e]" />
          <p className="text-sm font-medium text-[#777967]">Loading your holding...</p>
        </div>
      </div>
    );
  }

  if (loadError || !property || blocksHeld <= 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fdfbf5] px-6">
        <div className="w-full max-w-md rounded-[28px] border border-white/80 bg-white/80 p-8 text-center shadow-[0_25px_70px_rgba(55,92,32,0.08)]">
          <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1f2318]">
            Nothing to sell
          </h2>
          <p className="mt-3 text-sm text-[#777967]">
            {loadError || "You don't currently hold any tokens of this property."}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] px-6 py-3 text-sm font-semibold text-white shadow-lg"
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>
    );
  }

  if (existingListing && existingListing.active !== false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fdfbf5] px-6">
        <div className="w-full max-w-md rounded-[28px] border border-white/80 bg-white/80 p-8 text-center shadow-[0_25px_70px_rgba(55,92,32,0.08)]">
          <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1f2318]">
            You already have an active listing
          </h2>
          <p className="mt-3 text-sm text-[#777967]">
            {existingListing?.quantity != null || existingListing?.blocks != null ? (
              <>
                You're currently listing{" "}
                {formatNumber(existingListing?.quantity ?? existingListing?.blocks)}{" "}
                tokens of {property?.name} for sale.{" "}
              </>
            ) : (
              <>You already have an active listing for {property?.name}. </>
            )}
            Cancel it before creating a new one.
          </p>

          {submitError && (
            <p className="mt-3 text-[13px] font-medium text-red-500">{submitError}</p>
          )}

          <div className="mt-6 flex flex-col gap-3">
            {existingListing?.id ? (
              <button
                onClick={handleCancelExistingListing}
                disabled={isCancellingExisting}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] px-6 py-3 text-sm font-semibold text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isCancellingExisting ? "Cancelling..." : "Cancel Existing Listing"}
              </button>
            ) : (
              <button
                onClick={() =>
                  navigate(`/property-details/${property?.id}`, {
                    state: { id: property?.id },
                  })
                }
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] px-6 py-3 text-sm font-semibold text-white shadow-lg"
              >
                Manage Listing on Property Page
              </button>
            )}
            <button
              onClick={() => navigate(-1)}
              disabled={isCancellingExisting}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#d9ddcf] bg-white px-6 py-3 text-sm font-semibold text-[#52666f]"
            >
              <ArrowLeft size={16} />
              Back to Portfolio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfbf5] text-[#1f2318]">
      <main className="mx-auto w-full max-w-[1200px] px-5 py-8 sm:px-8">
        {/* Header + Stepper */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-[#6f7165]">
              Selling tokens — <span className="font-semibold text-[#1f2318]">{property?.name}</span>
            </p>

            <div className="flex items-center">
              {STEPS.map((s, idx) => {
                const num = idx + 1;
                const isDone = step > num;
                const isActive = step === num;
                return (
                  <React.Fragment key={s.key}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold ${
                          isDone || isActive
                            ? "bg-[#375c20] text-white"
                            : "bg-[#e6e8dc] text-[#9aa08c]"
                        }`}
                      >
                        {isDone ? <CheckCircle2 size={16} /> : num}
                      </div>
                      <span
                        className={`mt-1.5 text-[10px] font-bold uppercase tracking-[0.1em] ${
                          isActive ? "text-[#375c20]" : "text-[#9aa08c]"
                        }`}
                      >
                        {s.label}
                      </span>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div
                        className={`mx-2 mb-4 h-[2px] w-10 sm:w-16 ${
                          step > num ? "bg-[#375c20]" : "bg-[#e6e8dc]"
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="mt-4 h-[3px] w-full overflow-hidden rounded-full bg-[#e6e8dc]">
            <div
              className="h-full rounded-full bg-[#375c20] transition-all duration-500"
              style={{ width: `${((step - 1) / (STEPS.length - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* ===================== STEP 1: SELECT ===================== */}
        {step === 1 && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8a9463]">
              Step 1 of 3
            </p>
            <h1 className="mt-1 font-['Playfair_Display'] text-3xl font-semibold text-[#1f2318] sm:text-4xl">
              Select tokens to sell
            </h1>
            <p className="mt-2 text-[15px] text-[#6f7165]">
              Choose how many of your {formatNumber(blocksHeld)} tokens you'd
              like to list for resale.
            </p>

            <div className="mt-8 mx-auto max-w-[1000px] grid gap-6 lg:grid-cols-[1.3fr_1fr]">
              <div className="rounded-[24px] border border-white bg-white p-7 shadow-[0_18px_48px_rgba(76,122,46,0.08)]">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#8a9295]">
                    Tokens to sell
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setBlocksToSell((v) => Math.max(1, v - 1))}
                      className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#d9ddcf] text-[#4c7a2e] transition hover:bg-[#f1f4e9]"
                    >
                      <Minus size={13} />
                    </button>
                    <span className="min-w-[36px] text-center text-2xl font-bold text-[#1f2318]">
                      {blocksToSell}
                    </span>
                    <button
                      onClick={() =>
                        setBlocksToSell((v) => Math.min(blocksHeld, v + 1))
                      }
                      className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#d9ddcf] text-[#4c7a2e] transition hover:bg-[#f1f4e9]"
                    >
                      <Plus size={13} />
                    </button>
                  </div>
                </div>

                <input
                  type="range"
                  min={1}
                  max={Math.max(blocksHeld, 1)}
                  value={blocksToSell}
                  onChange={(e) => setBlocksToSell(Number(e.target.value))}
                  className="mt-6 w-full accent-[#4c7a2e]"
                />
                <div className="mt-1 flex justify-between text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9aa295]">
                  <span>1 token</span>
                  <span>{formatNumber(blocksHeld)} tokens (all)</span>
                </div>

                <p className="mt-7 text-[11px] font-bold uppercase tracking-[0.12em] text-[#8a9295]">
                  Quick select
                </p>
                <div className="mt-3 grid grid-cols-4 gap-2.5">
                  {[0.25, 0.5, 0.75].map((pct) => (
                    <button
                      key={pct}
                      onClick={() => setQuickSelect(pct)}
                      className="h-10 rounded-xl border border-[#d9ddcf] text-[13px] font-bold text-[#375c20] transition hover:bg-[#f1f4e9]"
                    >
                      {pct * 100}%
                    </button>
                  ))}
                  <button
                    onClick={() => setQuickSelect("all")}
                    className="h-10 rounded-xl border border-[#d9ddcf] text-[13px] font-bold text-[#375c20] transition hover:bg-[#f1f4e9]"
                  >
                    All
                  </button>
                </div>

                <p className="mt-7 text-[11px] font-bold uppercase tracking-[0.12em] text-[#8a9295]">
                  After this sale
                </p>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-[#f7f6ee] p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9aa295]">
                      Remaining tokens
                    </p>
                    <p className="mt-1 text-xl font-bold text-[#1f2318]">
                      {formatNumber(remainingBlocks)}
                    </p>
                  </div>
                  <div className="rounded-xl bg-[#f7f6ee] p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9aa295]">
                      Remaining ownership
                    </p>
                    <p className="mt-1 text-xl font-bold text-[#1f2318]">
                      {remainingOwnership.toFixed(2)}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Sale estimate */}
              <div className="flex flex-col gap-5">
                <div className="rounded-[24px] bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] p-7 text-white shadow-[0_24px_60px_rgba(76,122,46,0.22)]">
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/70">
                    Sale estimate
                  </p>
                  <p className="mt-3 text-[13px] text-white/70">Tokens selected</p>
                  <p className="text-4xl font-bold">{blocksToSell}</p>

                  <div className="mt-5 space-y-2 border-t border-white/15 pt-4 text-[13px]">
                    <div className="flex justify-between">
                      <span className="text-white/70">Price per token</span>
                      <span className="font-semibold">{formatCurrency(pricePerBlock)}</span>
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl bg-white/10 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-white/70">
                      You receive
                    </p>
                    <p className="mt-1 text-2xl font-bold">{formatCurrency(proceeds)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-2xl border border-white bg-white p-4 shadow-[0_10px_30px_rgba(76,122,46,0.06)]">
                  <Clock size={17} className="mt-0.5 shrink-0 text-[#4c7a2e]" />
                  <p className="text-[12px] leading-relaxed text-[#6f7165]">
                    <span className="font-semibold text-[#1f2318]">Market rate pricing.</span>{" "}
                    Tokens are priced at the current platform rate of{" "}
                    {formatCurrency(pricePerBlock)} per token.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-xl border border-[#d9ddcf] bg-white px-5 py-3 text-sm font-semibold text-[#52666f] transition hover:bg-[#f7f6ee]"
              >
                <ArrowLeft size={15} />
                Back to Portfolio
              </button>
              <button
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5"
              >
                Review Sale
                <ArrowUpRight size={15} />
              </button>
            </div>
          </>
        )}

        {/* ===================== STEP 2: REVIEW ===================== */}
        {step === 2 && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8a9463]">
              Step 2 of 3
            </p>
            <h1 className="mt-1 font-['Playfair_Display'] text-3xl font-semibold text-[#1f2318] sm:text-4xl">
              Review your sale
            </h1>
            <p className="mt-2 text-[15px] text-[#6f7165]">
              Please confirm the details below before proceeding.
            </p>

            <div className="mt-8 mx-auto max-w-2xl space-y-5">
              <div className="flex items-center justify-between rounded-[20px] border border-white bg-white p-5 shadow-[0_14px_40px_rgba(76,122,46,0.06)]">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#e9f0df] text-[#4c7a2e]">
                    <Building2 size={19} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#9aa295]">
                      Property
                    </p>
                    <p className="text-[15px] font-bold text-[#1f2318]">
                      {property?.name} · {property?.location}
                    </p>
                  </div>
                </div>
                <span className="rounded-full bg-[#fbf1dc] px-3 py-1.5 text-[11px] font-bold text-[#a1740f]">
                  Pending sale
                </span>
              </div>

              <div className="rounded-[20px] border border-white bg-white p-6 shadow-[0_14px_40px_rgba(76,122,46,0.06)]">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#9aa295]">
                  Sale breakdown
                </p>
                <div className="mt-3 divide-y divide-[#e6e6dc]">
                  <ReviewRow label="Tokens to sell" value={`${blocksToSell} tokens`} />
                  <ReviewRow
                    label="Ownership share being sold"
                    value={`${ownershipShareBeingSold.toFixed(3)}%`}
                  />
                  <ReviewRow label="Market rate per block" value={formatCurrency(pricePerBlock)} />
                  <ReviewRow label="Sale value" value={formatCurrency(grossValue)} />
                </div>
                <div className="mt-3 flex items-center justify-between rounded-xl bg-[#f7f6ee] px-4 py-3.5">
                  <span className="text-[13px] font-bold text-[#1f2318]">Proceeds to your wallet</span>
                  <span className="text-lg font-bold text-[#375c20]">{formatCurrency(proceeds)}</span>
                </div>
              </div>

              <div className="rounded-[20px] border border-white bg-white p-6 shadow-[0_14px_40px_rgba(76,122,46,0.06)]">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#9aa295]">
                  Settlement timeline
                </p>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  {[
                    { n: 1, title: "Listing created", desc: "Tokens listed on marketplace", tag: "Immediately" },
                    { n: 2, title: "Buyer matched", desc: "Platform matches a buyer", tag: "1-3 business days" },
                    { n: 3, title: "Funds released", desc: "AED credited to your wallet", tag: "After settlement" },
                  ].map((s) => (
                    <div key={s.n}>
                      <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-[#e9f0df] text-[13px] font-bold text-[#4c7a2e]">
                        {s.n}
                      </div>
                      <p className="mt-2 text-[13px] font-bold text-[#1f2318]">{s.title}</p>
                      <p className="mt-0.5 text-[11px] text-[#8a9295]">{s.desc}</p>
                      <span className="mt-2 inline-block rounded-full bg-[#e9f0df] px-2.5 py-1 text-[10px] font-bold text-[#4c7a2e]">
                        {s.tag}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-2xl border border-[#f0dfa8] bg-[#fdf6e3] p-4">
                <span className="mt-0.5 text-[#a1740f]">⚠</span>
                <p className="text-[12px] leading-relaxed text-[#7a5c14]">
                  <span className="font-bold">Important:</span> Once your tokens
                  are listed, you will not earn rent from them. The listing can
                  be cancelled before a buyer is matched.
                </p>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-2 rounded-xl border border-[#d9ddcf] bg-white px-5 py-3 text-sm font-semibold text-[#52666f] transition hover:bg-[#f7f6ee]"
              >
                <ArrowLeft size={15} />
                Edit Selection
              </button>
              <button
                onClick={() => setStep(3)}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5"
              >
                Confirm &amp; List
                <ArrowUpRight size={15} />
              </button>
            </div>
          </>
        )}

        {/* ===================== STEP 3: CONFIRM ===================== */}
        {step === 3 && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8a9463]">
              Step 3 of 3
            </p>
            <h1 className="mt-1 font-['Playfair_Display'] text-3xl font-semibold text-[#1f2318] sm:text-4xl">
              Confirm your listing
            </h1>
            <p className="mt-2 text-[15px] text-[#6f7165]">
              Review and accept the terms to list your tokens for sale.
              You'll approve this from your Phantom wallet.
            </p>

            <div className="mt-8 mx-auto max-w-2xl space-y-5">
              <div className="rounded-[24px] bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] p-8 text-center text-white shadow-[0_24px_60px_rgba(76,122,46,0.22)]">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/70">
                  Listing summary
                </p>
                <p className="mt-2 text-5xl font-bold">{blocksToSell}</p>
                <p className="mt-1 text-[14px] text-white/80">
                  tokens · {property?.name}, {property?.location}
                </p>
                <div className="mx-auto mt-4 inline-block rounded-2xl bg-white/10 px-6 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/70">
                    Expected proceeds
                  </p>
                  <p className="mt-1 text-2xl font-bold">{formatCurrency(proceeds)}</p>
                </div>
              </div>

              <div className="rounded-[20px] border border-white bg-white p-6 shadow-[0_14px_40px_rgba(76,122,46,0.06)]">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#9aa295]">
                  Terms &amp; conditions
                </p>
                <div className="mt-3 space-y-3">
                  {[
                    "Your tokens will be listed on the Terra marketplace for resale.",
                    "You will not receive rent income from tokens under active listing.",
                    "Proceeds will be credited to your wallet within 3 business days of settlement.",
                    "You may cancel this listing at any time before a buyer is matched.",
                  ].map((term, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#e9f0df] text-[#4c7a2e]">
                        <CheckCircle2 size={13} />
                      </div>
                      <p className="text-[13px] leading-relaxed text-[#465860]">{term}</p>
                    </div>
                  ))}
                </div>
              </div>

              <label className="flex items-start gap-3 rounded-xl border border-[#e5e7dc] bg-[#f7f6ee] p-4">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#4c7a2e]"
                />
                <span className="text-[13px] leading-relaxed text-[#465860]">
                  I understand and agree to the terms above, and confirm I wish
                  to list <span className="font-bold">{blocksToSell} tokens</span>{" "}
                  of {property?.name} for sale.
                </span>
              </label>

              {submitError && (
                <p className="text-[13px] font-medium text-red-500">{submitError}</p>
              )}

              <button
                onClick={handleListForSale}
                disabled={!agreedToTerms || isSubmitting}
                className="h-[52px] w-full rounded-2xl bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-[14px] font-bold text-white shadow-lg transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:from-[#a9b89a] disabled:via-[#a9b89a] disabled:to-[#a9b89a] disabled:opacity-70"
              >
                {isSubmitting ? "Listing..." : "List Tokens for Sale"}
              </button>

              <button
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
                className="h-[48px] w-full rounded-2xl border border-[#d9ddcf] bg-white text-[13px] font-semibold text-[#df3d3d] transition hover:bg-[#fff5f5]"
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {/* ===================== STEP 4: COMPLETE ===================== */}
        {step === 4 && listingResult && (
          <div className="mx-auto max-w-xl py-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#375c20] text-white">
              <CheckCircle2 size={30} />
            </div>
            <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#4c7a2e]">
              Listing confirmed
            </p>
            <h1 className="mt-2 font-['Playfair_Display'] text-3xl font-semibold text-[#1f2318] sm:text-4xl">
              Your tokens are listed.
            </h1>
            <p className="mt-3 text-[15px] leading-7 text-[#6f7165]">
              <span className="font-bold">{listingResult.blocks} tokens</span> of{" "}
              {property?.name} have been listed on the Terra marketplace.
              You'll be notified when a buyer is matched.
            </p>

            <div className="mt-7 rounded-[24px] bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] p-7 text-left text-white shadow-[0_24px_60px_rgba(76,122,46,0.22)]">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/70">
                Listing details
              </p>
              <div className="mt-3 divide-y divide-white/15 text-[13px]">
                <div className="flex justify-between py-2.5">
                  <span className="text-white/70">Property</span>
                  <span className="font-bold">{property?.name}, {property?.location}</span>
                </div>
                <div className="flex justify-between py-2.5">
                  <span className="text-white/70">Tokens listed</span>
                  <span className="font-bold">{listingResult.blocks}</span>
                </div>
                <div className="flex justify-between py-2.5">
                  <span className="text-white/70">Expected proceeds</span>
                  <span className="font-bold">{formatCurrency(listingResult.proceeds)}</span>
                </div>
                <div className="flex justify-between py-2.5">
                  <span className="text-white/70">Listing status</span>
                  <span className="font-bold">Active — awaiting buyer</span>
                </div>
                <div className="flex justify-between py-2.5">
                  <span className="text-white/70">Listing reference</span>
                  <span className="font-bold">#{listingResult.id}</span>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2 rounded-xl bg-white/10 px-4 py-3">
                <Phone size={14} className="shrink-0" />
                <p className="text-[12px] text-white/85">
                  We'll notify you by email when a buyer is matched.
                </p>
              </div>
            </div>

            {listingResult.signature && (
              <a
                href={`https://explorer.solana.com/tx/${listingResult.signature}?cluster=devnet`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#4c7a2e] hover:underline"
              >
                View transaction on explorer
                <ExternalLink size={12} />
              </a>
            )}

            <div className="mt-7 flex items-center justify-center gap-3">
              <button
                onClick={() =>
                  navigate("/customer/portfolio/details", { state: { id: property?.id } })
                }
                className="inline-flex items-center gap-2 rounded-xl border border-[#d9ddcf] bg-white px-5 py-3 text-sm font-semibold text-[#375c20] transition hover:bg-[#f7f6ee]"
              >
                <ArrowLeft size={15} />
                Back to Portfolio
              </button>
              <button
                onClick={() =>
                  navigate(`/property-details/${property?.id}`, {
                    state: { id: property?.id },
                  })
                }
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5"
              >
                View Marketplace
                <ExternalLink size={15} />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const ReviewRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-3">
    <span className="text-[13px] text-[#78898f]">{label}</span>
    <span className="text-[13px] font-bold text-[#1f2318]">{value}</span>
  </div>
);

export default SellBlocks;